/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import NonCompletionReasonPicker from '../../../../components/NonCompletionReasonPicker';
import {
   updateScheduleItemStatus,
   listCommissionEntries,
} from '../../../../services/routing/routingApi';

// =============================================================================
// Modal de transição de status.
//
// Comportamento conforme contrato backend Fase 1 (2026-05-05):
//   - Antes de abrir o modal, busca comissões existentes do item
//   - Sair de REALIZADO com N comissões → bloqueia a transição. Backend devolve
//     422 nessa tentativa; UI bloqueia o botão Confirmar antes pra evitar o
//     request inútil e orientar o operador a remover as comissões.
//   - Indo para NAO_REALIZADO → exige select de motivo
//   - Backend limpa non_completion_reason_id automaticamente quando vai pra
//     PENDENTE/REALIZADO — UI não precisa pedir confirmação extra
// =============================================================================

const STATUS_LABEL = {
   PENDENTE: 'Pendente',
   REALIZADO: 'Realizado',
   NAO_REALIZADO: 'Não realizado',
};

const Body = styled.div`
   font-size: 0.85rem;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   padding: 4px 0;
`;

const TransitionLine = styled.div`
   font-size: 0.85rem;
   margin-bottom: 12px;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};

   strong {
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
      font-weight: 600;
   }
`;

const BlockBox = styled.div`
   background: rgba(211, 47, 47, 0.08);
   border: 1px solid rgba(211, 47, 47, 0.4);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.8rem;
   color: #b71c1c;
   margin-bottom: 12px;
   line-height: 1.5;
`;

// Box âmbar do gate de freelance (§6) e do aviso de rota não atribuída.
const FreelanceBox = styled.div`
   background: rgba(245, 158, 11, 0.08);
   border: 1px solid rgba(245, 158, 11, 0.5);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.8rem;
   color: #92400e;
   margin-bottom: 12px;
   line-height: 1.5;

   strong {
      font-weight: 700;
   }
`;

const PickerWrap = styled.div`
   margin-top: 12px;
`;

const ButtonRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 4px;
`;

// =============================================================================

export default function StatusChangeModal({
   isOpen,
   onClose,
   item,
   targetStatus,
   onStatusChanged,
}) {
   const [reasonId, setReasonId] = useState('');
   const [commissionsCount, setCommissionsCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   // Gate de freelance (handoff 0042 §6): concluir (REALIZADO) item sem técnico
   // atribuído exige confirmação explícita → re-envia com freelance_confirmed.
   const [freelanceRequired, setFreelanceRequired] = useState(false);
   // Rota com técnico designado mas não atribuída — orienta a atribuir antes.
   const [routeNotAssigned, setRouteNotAssigned] = useState(false);

   // Guarda do unmount: evita setState após fechar
   const mountedRef = useRef(true);
   useEffect(() => {
      mountedRef.current = true;
      return () => {
         mountedRef.current = false;
      };
   }, []);

   // Quando o modal abre: pre-fetch dados necessários
   useEffect(() => {
      if (!isOpen || !item || !targetStatus) return undefined;

      let cancelled = false;
      setIsLoading(true);
      setReasonId('');
      setCommissionsCount(0);
      setFreelanceRequired(false);
      setRouteNotAssigned(false);

      const tasks = [];

      // Catálogo de motivos é responsabilidade do NonCompletionReasonPicker
      // (auto-fetch interno + cache module-level). Não pré-buscamos aqui.

      // Quando SAI de REALIZADO, calcula quantas comissões serão "preservadas mas bloqueadas"
      if (item.item_status === 'REALIZADO' && targetStatus !== 'REALIZADO') {
         tasks.push(
            listCommissionEntries(item.id)
               .then((entries) => {
                  if (!cancelled) setCommissionsCount(entries.length);
               })
               .catch(() => {
                  if (!cancelled) setCommissionsCount(0);
               })
         );
      }

      Promise.all(tasks).finally(() => {
         if (!cancelled) setIsLoading(false);
      });

      return () => {
         cancelled = true;
      };
   }, [isOpen, item, targetStatus]);

   // useCallback obrigatório no onClose (evita focus loss recorrente em modais MyShop)
   const handleClose = useCallback(() => {
      if (isSubmitting) return;
      onClose?.();
   }, [isSubmitting, onClose]);

   // ─── Submit ─────────────────────────────────────────────────
   const handleConfirm = async () => {
      if (isSubmitting) return;

      // Guard: NAO_REALIZADO exige motivo
      if (targetStatus === 'NAO_REALIZADO' && !reasonId) {
         toast.warn('Selecione o motivo de não realização.');
         return;
      }

      setIsSubmitting(true);
      setRouteNotAssigned(false);
      try {
         const payload = {
            item_status: targetStatus,
            non_completion_reason_id:
               targetStatus === 'NAO_REALIZADO' ? Number(reasonId) : null,
         };
         // Só carimba freelance após o operador confirmar (gate §6).
         if (freelanceRequired) payload.freelance_confirmed = true;
         const result = await updateScheduleItemStatus(item.id, payload);
         toast.success(result.message || 'Status atualizado com sucesso.');
         onStatusChanged?.(result.scheduleItem);
         onClose?.();
      } catch (err) {
         const code = err?.response?.data?.errors?.[0]?.code;
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao atualizar status.';
         // Gate de freelance (§6): item sem técnico → pede confirmação e re-envia.
         if (code === 'freelance_confirmation_required') {
            setFreelanceRequired(true);
         } else if (code === 'route_not_assigned') {
            // Rota com técnico designado mas não atribuída — orienta no board.
            setRouteNotAssigned(true);
         } else {
            toast.error(apiMessage);
         }
      } finally {
         if (mountedRef.current) setIsSubmitting(false);
      }
   };

   if (!isOpen || !item || !targetStatus) return null;

   const isLeavingRealized =
      item.item_status === 'REALIZADO' && targetStatus !== 'REALIZADO';

   // Backend bloqueia regressão de REALIZADO com comissões — UI replica o guard
   // pra evitar request inútil e dar feedback imediato ao operador.
   const isBlockedByCommissions = isLeavingRealized && commissionsCount > 0;

   return (
      <Modal
         key={isOpen ? 'open' : 'closed'}
         isOpen={isOpen}
         onClose={handleClose}
         title="Confirmar mudança de status"
         width="480px"
      >
         <Body>
            <TransitionLine>
               Status do item vai mudar de{' '}
               <strong>{STATUS_LABEL[item.item_status] ?? item.item_status}</strong>{' '}
               para{' '}
               <strong>{STATUS_LABEL[targetStatus] ?? targetStatus}</strong>.
            </TransitionLine>

            {isLoading && (
               <Body style={{ opacity: 0.7 }}>Verificando dependências…</Body>
            )}

            {!isLoading && isBlockedByCommissions && (
               <BlockBox>
                  <strong>Não é possível regredir o status:</strong> este item
                  tem <strong>{commissionsCount}</strong>{' '}
                  {commissionsCount === 1
                     ? 'comissão lançada'
                     : 'comissões lançadas'}
                  . Remova{' '}
                  {commissionsCount === 1 ? 'a comissão' : 'as comissões'} no
                  painel abaixo antes de mudar o status.
               </BlockBox>
            )}

            {!isLoading && routeNotAssigned && (
               <FreelanceBox>
                  Esta rota tem técnico <strong>designado mas não atribuído</strong>.
                  Atribua a rota no quadro de roteirização (botão{' '}
                  <strong>Atribuir rota</strong>) antes de concluir o item.
               </FreelanceBox>
            )}

            {!isLoading && freelanceRequired && (
               <FreelanceBox>
                  A conclusão deste item sem técnico atribuído registrará o
                  atendimento como <strong>técnico freelance</strong>. Considere
                  renomear a rota com o nome do freelance para facilitar consultas
                  futuras. Clique em <strong>Confirmar como freelance</strong> para
                  prosseguir.
               </FreelanceBox>
            )}

            {!isLoading && targetStatus === 'NAO_REALIZADO' && (
               <PickerWrap>
                  <NonCompletionReasonPicker
                     value={reasonId}
                     onChange={setReasonId}
                     disabled={isSubmitting}
                     required
                     labelText="Motivo de não realização"
                  />
               </PickerWrap>
            )}

            <ButtonRow>
               <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </button>
               <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={
                     isSubmitting ||
                     isLoading ||
                     isBlockedByCommissions ||
                     (targetStatus === 'NAO_REALIZADO' && !reasonId)
                  }
                  title={
                     isBlockedByCommissions
                        ? 'Remova as comissões antes de regredir o status.'
                        : ''
                  }
               >
                  {(() => {
                     if (isSubmitting) return 'Confirmando…';
                     if (freelanceRequired) return 'Confirmar como freelance';
                     return 'Confirmar';
                  })()}
               </button>
            </ButtonRow>
         </Body>
      </Modal>
   );
}

StatusChangeModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   item: PropTypes.object,
   targetStatus: PropTypes.oneOf(['PENDENTE', 'REALIZADO', 'NAO_REALIZADO']),
   onStatusChanged: PropTypes.func,
};
